/**
 * SEO route definitions for team / group / platform landing pages.
 *
 * Single source of truth so the React app (wouter routes), the prerender
 * script (script/prerender-locales.mjs) and the sitemap generator stay in
 * sync.
 *
 * Team and group URLs are year-prefixed (`/teams/2026/<slug>`,
 * `/groups/2026/<letter>`) so each edition of the tournament gets a clean
 * archival surface. Older editions stay live as permanent archives —
 * adding 2030 produces `/teams/2030/<slug>` alongside `/teams/2026/<slug>`
 * without overwriting the 2026 pages. Platform pages stay evergreen
 * (`/platforms/<slug>`) since the underlying app instructions outlive any
 * single edition; copy on those pages cycles each tournament.
 *
 * Selective localisation: each team gets English plus its native locale(s)
 * only — we do not blindly duplicate every page into every locale. Platform
 * pages are universally useful, so they ship in all 16 locales. Group pages
 * inherit the union of the native locales of the four teams in the group.
 */

import {
  EDITIONS,
  CURRENT_EDITION,
  TOURNAMENT_YEAR,
  ALL_TOURNAMENT_YEARS,
  getEdition,
} from "../server/editions";
import type { Edition, EditionTeam } from "../server/editions";
import type { SupportedLanguage } from "../client/src/i18n";

// Re-export so existing callers keep working.
export { TOURNAMENT_YEAR, ALL_TOURNAMENT_YEARS, EDITIONS, CURRENT_EDITION, getEdition };
export type { Edition, EditionTeam };

export type GroupLetter =
  | "A" | "B" | "C" | "D" | "E" | "F"
  | "G" | "H" | "I" | "J" | "K" | "L";

export interface TeamRoute {
  /** ISO-ish three-letter team code. */
  code: string;
  /** URL slug under "/teams/<year>/". */
  slug: string;
  /** Tournament year this route belongs to. */
  year: number;
  /** Locales (in addition to "en") that get a fully translated page. */
  localizedLocales: ReadonlyArray<SupportedLanguage>;
}

export interface GroupRoute {
  letter: GroupLetter;
  /** Tournament year this route belongs to. */
  year: number;
  /** Team codes (4) in the group, in the order they appear on the page. */
  teamCodes: ReadonlyArray<string>;
  /** Locales (in addition to "en") that get a fully translated page. */
  localizedLocales: ReadonlyArray<SupportedLanguage>;
}

export interface PlatformRoute {
  /** URL slug under "/platforms/". */
  slug: string;
  /** "google" | "apple" | "outlook" — for HowTo step variant. */
  platform: "google" | "apple" | "outlook";
  /** Locales (in addition to "en") that get a fully translated page. */
  localizedLocales: ReadonlyArray<SupportedLanguage>;
}

const ALL_NON_EN_LOCALES: ReadonlyArray<SupportedLanguage> = [
  "es", "ar", "fr", "pt", "de", "nl", "hr",
  "fa", "cs", "no", "sv", "ko", "ja", "tr", "uz",
];

/**
 * Per-team additional locales beyond English. Keyed by team code so the
 * authoritative team list in `server/editions/<year>.ts` remains the
 * only source of truth for names, flags, groups and confederations.
 *
 * Any code that doesn't appear here defaults to English-only — which is
 * the right behaviour for new teams in future editions until someone
 * explicitly opts them into a localised surface.
 */
const TEAM_LOCALES: Record<string, ReadonlyArray<SupportedLanguage>> = {
  // Spanish-speaking
  ESP: ["es"],
  MEX: ["es"],
  ARG: ["es"],
  ECU: ["es"],
  PAR: ["es"],
  URU: ["es"],
  COL: ["es"],
  // Lusophone
  BRA: ["pt"],
  POR: ["pt"],
  CPV: ["pt"],
  // French-speaking
  FRA: ["fr"],
  SEN: ["fr"],
  CIV: ["fr"],
  HAI: ["fr"],
  BEL: ["fr", "nl"],
  // German-speaking
  GER: ["de"],
  AUT: ["de"],
  SUI: ["de"],
  // Dutch-speaking (Belgium covered above)
  NED: ["nl"],
  CUW: ["nl"],
  // Other Europeans
  CRO: ["hr"],
  CZE: ["cs"],
  NOR: ["no"],
  SWE: ["sv"],
  // Asian
  KOR: ["ko"],
  JPN: ["ja"],
  TUR: ["tr"],
  UZB: ["uz"],
  IRN: ["fa"],
  // Arabic-speaking
  EGY: ["ar"],
  KSA: ["ar"],
  QAT: ["ar"],
  TUN: ["ar"],
  MAR: ["ar"],
  // English-only (no additional locales)
  RSA: [],
  CAN: [],
  BIH: [],
  AUS: [],
  USA: [],
  SCO: [],
  IRQ: [],
  ALG: [],
  JOR: [],
  COD: [],
  ENG: [],
  GHA: [],
  PAN: [],
  NZL: [],
};

/**
 * Strip diacritics and produce a stable, lowercase URL slug for a team name.
 *
 * Canonical slugs use plain ASCII (so "Curaçao" -> "curacao"). Common
 * alternative transliterations live in `teamAliasSlugs` below.
 */
export function slugifyTeam(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, "-");
}

/**
 * Return common alternative slugs for a team name (excluding the canonical
 * slug). Handles the most likely inbound link variants so they don't 404.
 */
export function teamAliasSlugs(name: string): string[] {
  const canonical = slugifyTeam(name);
  const aliases = new Set<string>();

  // "Korea" rather than "South Korea" is the FIFA short name.
  if (name === "South Korea") aliases.add("korea");
  // "USA" / "US" rather than "United States".
  if (name === "United States") {
    aliases.add("usa");
    aliases.add("us");
  }
  // "Czech Republic" rather than "Czechia".
  if (name === "Czechia") aliases.add("czech-republic");
  // "Bosnia and Herzegovina" — Wikipedia and most search queries use the
  // long form with "and", while our data uses the hyphenated short form.
  if (name === "Bosnia-Herzegovina") {
    aliases.add("bosnia-and-herzegovina");
    aliases.add("bosnia");
  }
  // "Cote d'Ivoire" (Francophone official) and "Ivory Coast" (Anglophone).
  if (name === "Ivory Coast") aliases.add("cote-d-ivoire");
  // "Cape Verde" vs "Cabo Verde" (official UN name).
  if (name === "Cape Verde") aliases.add("cabo-verde");
  // "Curaçao" with the cedilla preserved or stripped.
  if (name === "Curaçao") aliases.add("curaco");
  // "DR Congo" / "Congo-Kinshasa" common shortenings.
  if (name === "Congo DR") {
    aliases.add("dr-congo");
    aliases.add("democratic-republic-of-congo");
  }
  // "Turkiye" — the official ISO romanisation Turkey switched to in 2022.
  if (name === "Turkey") aliases.add("turkiye");

  aliases.delete(canonical);
  return Array.from(aliases);
}

/** Build TEAM_ROUTES for every team in every known edition. */
export const TEAM_ROUTES: TeamRoute[] = EDITIONS.flatMap((edition) =>
  edition.teams.map((team) => ({
    code: team.code,
    slug: slugifyTeam(team.name),
    year: edition.year,
    localizedLocales: TEAM_LOCALES[team.code] ?? [],
  })),
);

/**
 * Build GROUP_ROUTES for every group letter in every known edition.
 * For each (year, letter), derive the team codes and the union of
 * additional locales of the four teams. English is always present.
 */
export const GROUP_ROUTES: GroupRoute[] = EDITIONS.flatMap((edition) => {
  const letters: GroupLetter[] = ["A","B","C","D","E","F","G","H","I","J","K","L"];
  return letters.map((letter) => {
    const teams = edition.teams.filter((t) => t.group === letter);
    const localeSet = new Set<SupportedLanguage>();
    for (const t of teams) {
      for (const locale of TEAM_LOCALES[t.code] ?? []) localeSet.add(locale);
    }
    return {
      letter,
      year: edition.year,
      teamCodes: teams.map((t) => t.code),
      localizedLocales: Array.from(localeSet),
    };
  });
});

/**
 * Platform pages are universally useful regardless of which team the
 * visitor follows, so they ship in every supported locale.
 */
export const PLATFORM_ROUTES: PlatformRoute[] = [
  { slug: "apple-calendar-world-cup-fixtures",   platform: "apple",   localizedLocales: ALL_NON_EN_LOCALES },
  { slug: "google-calendar-world-cup-fixtures",  platform: "google",  localizedLocales: ALL_NON_EN_LOCALES },
  { slug: "outlook-world-cup-fixtures",          platform: "outlook", localizedLocales: ALL_NON_EN_LOCALES },
];

export const SEO_ROUTE_PREFIX = {
  teams: "teams",       // /teams, /teams/<year>/<slug>
  groups: "groups",     // /groups, /groups/<year>/<letter>
  platforms: "platforms", // /platforms, /platforms/<slug>
} as const;

/**
 * Build the year-prefixed team detail URL. Locale prefix is added by the
 * caller (`localePath`). Defaults to the current edition.
 */
export function teamPath(slug: string, year: number = TOURNAMENT_YEAR): string {
  return `/teams/${year}/${slug}`;
}

/**
 * Build the year-prefixed group detail URL. The letter is lowercased.
 * Defaults to the current edition.
 */
export function groupPath(letter: string, year: number = TOURNAMENT_YEAR): string {
  return `/groups/${year}/${letter.toLowerCase()}`;
}

/**
 * Build the evergreen platform detail URL.
 */
export function platformPath(slug: string): string {
  return `/platforms/${slug}`;
}
